import { Box, Divider, Drawer, IconButton, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import { L10nHTML, useIsMobile, useL10n } from '@uplift/hooks';
import { ContactType, UserContact } from '@uplift/types';
import { FormModal, ModalActions, ModalContent, ModalTitle, PhoneMask, RadioControl, SubmitButton } from '@uplift/ui-v2';
import { Form, Formik } from 'formik';
import { useCallback, useMemo } from 'react';
import { object, string } from 'yup';

type FormType = {
  contact?: string;
};

const validation = object({
  contact: string().required()
});

export type SelectFactorModalProps = {
  open: boolean;
  contacts: UserContact[];
  email: string;
  onClose: () => void;
  onSubmit: (id: string) => void;
};

export const SelectFactorModal: React.VFC<SelectFactorModalProps> = ({ open, contacts, email, onClose, onSubmit }) => {
  const isMobile = useIsMobile('sm');
  const l10n = useL10n();

  const radios = useMemo(() => {
    const emailRadio = [
      {
        value: 'email',
        label: (
          <L10nHTML>
            {l10n.auth.mfa.emailIt(email)}
          </L10nHTML>
        )
      }
    ];

    return emailRadio.concat(
      contacts.filter(x => x.type === ContactType.phone)
        .map(x => ({
          value: x.id,
          label: (
            <>
              <L10nHTML>{l10n.auth.mfa.textIt}</L10nHTML> <strong><PhoneMask phone={x.value} /></strong>
            </>
          )
        })));
  }, [ contacts, email, l10n.auth.mfa ]);

  const handleSubmit = useCallback(values => {
    onSubmit(values.contact);
  }, [ onSubmit ]);

  const initialValues: FormType = {};

  if (isMobile) {
    return (
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validation}
      >
        {({ values }) => (
          <Form>
            <Drawer anchor="bottom" open>
              <Box pl={3} pr={2} py={2} display="flex" justifyContent="space-between">
                <Typography variant="h6">
                  {l10n.auth.mfa.whereShouldWeSend}
                </Typography>
                <IconButton aria-label="close" onClick={onClose} size="small">
                  <Close />
                </IconButton>
              </Box>
              <Divider />
              <Box pl={3} pt={3} pb={5}>
                <RadioControl
                  name="contact"
                  values={radios}
                />
              </Box>
              <Divider />
              <Box px={3} py={2}>
                <SubmitButton color="secondary" disabled={!values.contact} fullWidth>
                  {l10n.auth.mfa.sendCode}
                </SubmitButton>
              </Box>
            </Drawer>
          </Form>
        )}
      </Formik>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validation}
    >
      {({ values }) => (
        <FormModal
          open={open}
          maxWidth="md"
          fullWidth
        >
          <ModalTitle onClose={onClose}>
            {l10n.auth.mfa.whereShouldWeSend}
          </ModalTitle>
          <ModalContent>
            <RadioControl
              name="contact"
              values={radios}
            />
          </ModalContent>
          <ModalActions>
            <SubmitButton color="secondary" disabled={!values.contact}>
              {l10n.auth.mfa.sendCode}
            </SubmitButton>
          </ModalActions>
        </FormModal>
      )}
    </Formik>
  );
};
